import { MessagePrimitive, ThreadPrimitive, useAssistantState } from "@assistant-ui/react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import React, { createContext, useContext } from "react";
import ChatMarkdownText from "./ChatMarkdownText";
import ChatTypingIndicator from "./ChatTypingIndicator";

interface ChatThreadProps {
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  /** Replaces the typing dots after `slowResponseNoticeDelayMs` of waiting on a response. */
  slowResponseNotice?: string;
  slowResponseNoticeDelayMs?: number;
}

// The message components are handed to ThreadPrimitive.Messages without props, so the
// typing-indicator copy reaches them via context instead.
const SlowResponseConfigContext = createContext<{
  slowResponseNotice?: string;
  slowResponseNoticeDelayMs?: number;
}>({});

/**
 * Scrollable message list with auto-scroll, an empty state, markdown-rendered assistant
 * messages, and a typing indicator inside the pending assistant bubble while a response
 * is being prepared. Must be rendered inside an `AssistantRuntimeProvider`.
 */
const ChatThread = ({
  emptyStateTitle = "How can I help?",
  emptyStateSubtitle,
  slowResponseNotice,
  slowResponseNoticeDelayMs,
}: ChatThreadProps) => {
  return (
    <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
      <ThreadPrimitive.Viewport className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-4">
        <ThreadPrimitive.Empty>
          <div className="flex flex-1 flex-col items-center justify-center px-3 text-center">
            <SparklesIcon className="mb-3 h-8 w-8 text-neutral-300" />
            <p className="text-[15px] font-medium text-neutral-900">{emptyStateTitle}</p>
            {emptyStateSubtitle && (
              <p className="mt-1 text-[13px] leading-5 text-neutral-500">{emptyStateSubtitle}</p>
            )}
          </div>
        </ThreadPrimitive.Empty>
        <SlowResponseConfigContext.Provider
          value={{ slowResponseNotice, slowResponseNoticeDelayMs }}
        >
          <ThreadPrimitive.Messages
            components={{ UserMessage: ChatUserMessage, AssistantMessage: ChatAssistantMessage }}
          />
        </SlowResponseConfigContext.Provider>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ChatUserMessage = () => (
  <MessagePrimitive.Root className="flex justify-end">
    <div className="max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary-600 px-3 py-2 text-sm leading-5 text-white">
      <MessagePrimitive.Parts />
    </div>
  </MessagePrimitive.Root>
);

const ChatAssistantMessage = () => {
  const slowResponseConfig = useContext(SlowResponseConfigContext);
  const isError = useAssistantState(({ message }) => message.metadata?.custom?.isError === true);
  // The external-store runtime appends an empty optimistic assistant message while a run is
  // in flight; render the typing indicator inside that bubble until the first token arrives.
  const isAwaitingResponse = useAssistantState(
    ({ message }) =>
      message.role === "assistant" &&
      message.status?.type === "running" &&
      message.content.length === 0,
  );

  return (
    <MessagePrimitive.Root className="flex justify-start">
      <div
        className={
          isError
            ? "max-w-[85%] break-words rounded-lg bg-red-50 px-3 py-2 text-sm leading-5 text-red-600"
            : "max-w-[85%] break-words rounded-lg bg-neutral-100 px-3 py-2 text-sm leading-5 text-neutral-900"
        }
      >
        {isAwaitingResponse ? (
          <ChatTypingIndicator
            slowResponseNotice={slowResponseConfig.slowResponseNotice}
            slowResponseNoticeDelayMs={slowResponseConfig.slowResponseNoticeDelayMs}
          />
        ) : (
          <MessagePrimitive.Parts components={{ Text: ChatMarkdownText }} />
        )}
      </div>
    </MessagePrimitive.Root>
  );
};

export default ChatThread;
